import { Suspense, lazy, useEffect } from "react";
import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CookieConsentProvider } from "@/components/cookie-consent-provider";
import { CookieBanner } from "@/components/cookie-banner";
import { FloatingContact } from "@/components/floating-contact";
import { PwaPrompt } from "@/components/pwa-prompt";
import { isPrerendering } from "@/lib/prerender";

// Per-route code splitting. Every page is its own chunk so any single landing
// (notably /erp-conversazionale) only ships its own JS instead of the whole
// site's worth of pages. The prerender script (script/prerender-ssr.ts) waits
// on `networkidle` plus a settle, so dynamic imports resolve before the SSR
// snapshot is taken — this keeps SEO content intact.
const Home = lazy(() => import("@/pages/home"));
const Services = lazy(() => import("@/pages/services"));
const ServiceDetail = lazy(() => import("@/pages/services/detail"));
const GoogleAds = lazy(() => import("@/pages/google-ads"));
const About = lazy(() => import("@/pages/about"));
const Contact = lazy(() => import("@/pages/contact"));
const Quote = lazy(() => import("@/pages/quote"));
const QuoteThanks = lazy(() => import("@/pages/quote-thanks"));
const ErpConversazionale = lazy(() => import("@/pages/erp-conversazionale"));
const Insights = lazy(() => import("@/pages/insights"));
const InsightDetail = lazy(() => import("@/pages/insight-detail"));
const CaseStudyIndex = lazy(() => import("@/pages/case-study/index"));
const CaseStudyDetail = lazy(() => import("@/pages/case-study/detail"));
const BrandValorization = lazy(() => import("@/pages/valorizzazione-marchio"));
// Funnel Google Ads: landing verticali con form in pagina + thank-you unica.
const FunnelPeriziaGenerale = lazy(() => import("@/pages/funnel/perizia-generale"));
const FunnelPeriziaConferimento = lazy(() => import("@/pages/funnel/perizia-conferimento"));
const FunnelPeriziaCessione = lazy(() => import("@/pages/funnel/perizia-cessione"));
const FunnelPeriziaRoyalty = lazy(() => import("@/pages/funnel/perizia-royalty"));
const FunnelAuditGoogleAds = lazy(() => import("@/pages/funnel/audit-google-ads"));
const FunnelCrmPmi = lazy(() => import("@/pages/funnel/crm-pmi"));
const FunnelPortaleClienti = lazy(() => import("@/pages/funnel/portale-clienti"));
const FunnelGestionaleSuMisura = lazy(() => import("@/pages/funnel/gestionale-su-misura"));
const FunnelThankYou = lazy(() => import("@/pages/funnel/thank-you"));
const Privacy = lazy(() => import("@/pages/privacy"));
const CookiePolicy = lazy(() => import("@/pages/cookie-policy"));
const Terms = lazy(() => import("@/pages/terms"));
const LegalNotice = lazy(() => import("@/pages/legal-notice"));
const Newsletter = lazy(() => import("@/pages/newsletter"));
const NewsletterConfirmed = lazy(() => import("@/pages/newsletter-confirmed"));
const NewsletterUnsubscribed = lazy(() => import("@/pages/newsletter-unsubscribed"));
const Status = lazy(() => import("@/pages/status"));
const StatusConfirmed = lazy(() => import("@/pages/status-confirmed"));
const StatusUnsubscribed = lazy(() => import("@/pages/status-unsubscribed"));
const NotFound = lazy(() => import("@/pages/not-found"));
const AdminLogin = lazy(() => import("@/pages/admin/login"));
const AdminDashboard = lazy(() => import("@/pages/admin/dashboard"));
const AdminLeads = lazy(() => import("@/pages/admin/leads"));
const AdminTargeting = lazy(() => import("@/pages/admin/targeting"));
const AdminUsers = lazy(() => import("@/pages/admin/users"));
const AdminIntegrations = lazy(() => import("@/pages/admin/integrations"));
const AdminInsights = lazy(() => import("@/pages/admin/insights"));
const AdminGoogleAds = lazy(() => import("@/pages/admin/google-ads"));
const AdminBrandValorization = lazy(
  () => import("@/pages/admin/valorizzazione-marchio"),
);
const AdminNewsletter = lazy(() => import("@/pages/admin/newsletter"));
const AdminPreventivi = lazy(() => import("@/pages/admin/preventivi"));
const AdminClienti = lazy(() => import("@/pages/admin/clienti"));
const AdminPreventivoNuovo = lazy(() => import("@/pages/admin/preventivo-nuovo"));
const AdminPreventivoDettaglio = lazy(() => import("@/pages/admin/preventivo-dettaglio"));
const PublicQuote = lazy(() => import("@/pages/public-quote"));
const AdminContratti = lazy(() => import("@/pages/admin/contratti"));
const AdminWhatsapp = lazy(() => import("@/pages/admin/whatsapp"));
const AdminEmailResend = lazy(() => import("@/pages/admin/email-resend"));
const AdminPerizieMarchio = lazy(() => import("@/pages/admin/perizie-marchio"));
const AdminMarketing = lazy(() => import("@/pages/admin/marketing"));
const AdminContenuti = lazy(() => import("@/pages/admin/contenuti"));

// Empty Suspense fallback: each page reserves its own viewport so a flash of
// nothing for one tick is preferable to layout-shifting placeholder chrome.
// SSR snapshots are injected into #root before React mounts, so first-paint
// always shows real content even before the lazy chunk resolves.
function RouteFallback() {
  return null;
}

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location]);
  return null;
}

function Router() {
  const [location] = useLocation();
  const isAdmin = location === "/admin" || location.startsWith("/admin/");

  if (isAdmin) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Switch>
          <Route path="/admin/login" component={AdminLogin} />
          <Route path="/admin" component={AdminDashboard} />
          <Route path="/admin/lead" component={AdminLeads} />
          <Route path="/admin/targeting" component={AdminTargeting} />
          <Route path="/admin/utenti" component={AdminUsers} />
          <Route path="/admin/integrazioni" component={AdminIntegrations} />
          <Route path="/admin/insights" component={AdminInsights} />
          <Route path="/admin/google-ads" component={AdminGoogleAds} />
          <Route
            path="/admin/valorizzazione-marchio"
            component={AdminBrandValorization}
          />
          <Route path="/admin/newsletter" component={AdminNewsletter} />
          <Route path="/admin/clienti" component={AdminClienti} />
          <Route path="/admin/preventivi/nuovo" component={AdminPreventivoNuovo} />
          <Route path="/admin/preventivi/:id" component={AdminPreventivoDettaglio} />
          <Route path="/admin/preventivi" component={AdminPreventivi} />
          <Route path="/admin/contratti" component={AdminContratti} />
          <Route path="/admin/whatsapp" component={AdminWhatsapp} />
          <Route path="/admin/email" component={AdminEmailResend} />
          <Route path="/admin/perizie-marchio" component={AdminPerizieMarchio} />
          <Route path="/admin/marketing" component={AdminMarketing} />
          <Route path="/admin/contenuti" component={AdminContenuti} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    );
  }

  // No AnimatePresence / motion wrapper around the routes. The previous
  // `motion.div` did `opacity 0 → 1` on hydration, which momentarily hid the
  // prerendered LCP element and kept Lighthouse's LCP attribution waiting on
  // the framer-motion transition (~300 ms simulated, much more under
  // simulated slow-4G / 4× CPU). Removing it lets the SSR-injected hero
  // count as LCP immediately on hydration.
  return (
    <Suspense fallback={<RouteFallback />}>
      <Switch location={location} key={location}>
        <Route path="/" component={Home} />
        {/* Funnel Google Ads — software: registrate PRIMA di /servizi/google-ads
            e di /servizi/:slug per non collidere con la route generica. */}
        <Route path="/servizi/crm-pmi" component={FunnelCrmPmi} />
        <Route path="/servizi/portale-clienti" component={FunnelPortaleClienti} />
        <Route
          path="/servizi/gestionale-su-misura"
          component={FunnelGestionaleSuMisura}
        />
        <Route path="/servizi" component={Services} />
        <Route path="/servizi/google-ads" component={GoogleAds} />
        <Route path="/servizi/:slug" component={ServiceDetail} />
        <Route path="/chi-siamo" component={About} />
        <Route path="/contatti" component={Contact} />
        <Route path="/preventivo" component={Quote} />
        <Route path="/preventivo/grazie" component={QuoteThanks} />
        <Route path="/preventivo/v/:token" component={PublicQuote} />
        <Route path="/erp-conversazionale" component={ErpConversazionale} />
        <Route path="/insights" component={Insights} />
        <Route path="/insights/:slug" component={InsightDetail} />
        <Route path="/case-study" component={CaseStudyIndex} />
        <Route path="/case-study/:slug" component={CaseStudyDetail} />
        <Route path="/valorizzazione-marchio" component={BrandValorization} />
        {/* Funnel Google Ads — perizie marchio + audit ads. */}
        <Route
          path="/valorizzazione-marchio/perizia"
          component={FunnelPeriziaGenerale}
        />
        <Route
          path="/valorizzazione-marchio/conferimento"
          component={FunnelPeriziaConferimento}
        />
        <Route
          path="/valorizzazione-marchio/cessione"
          component={FunnelPeriziaCessione}
        />
        <Route
          path="/valorizzazione-marchio/royalty"
          component={FunnelPeriziaRoyalty}
        />
        <Route path="/audit-google-ads" component={FunnelAuditGoogleAds} />
        {/* Thank-you unica funnel: legge :funnel, redirect a / se sconosciuto. */}
        <Route path="/grazie/:funnel" component={FunnelThankYou} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/cookie-policy" component={CookiePolicy} />
        <Route path="/termini" component={Terms} />
        <Route path="/note-legali" component={LegalNotice} />
        <Route path="/newsletter" component={Newsletter} />
        <Route path="/newsletter/confirmato" component={NewsletterConfirmed} />
        <Route path="/newsletter/disiscritto" component={NewsletterUnsubscribed} />
        <Route path="/status" component={Status} />
        <Route path="/status/confirmato" component={StatusConfirmed} />
        <Route path="/status/disiscritto" component={StatusUnsubscribed} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function PublicChrome() {
  const [location] = useLocation();
  const isAdmin = location === "/admin" || location.startsWith("/admin/");
  if (isAdmin) return null;
  if (isPrerendering()) return null;
  return (
    <>
      <FloatingContact />
      <CookieBanner />
      <PwaPrompt />
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <CookieConsentProvider>
          <ScrollToTop />
          <Toaster />
          <PublicChrome />
          <Router />
        </CookieConsentProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
